/* ==========================================================================
   HEADER SEARCH SUGGESTIONS (loaded with defer from layout/theme.liquid, after header.js).
   Markup: snippets/header-search-panel.liquid (#HeaderSearchPanel, rendered once in .hdr-sticky);
   typed results: sections/header-search-results.liquid through Shopify's predictive search
   (Section Rendering API). Styles: assets/header.css ("Header search suggestions").
   Desktop (>= 1024px): dropdown under the header field. Mobile: full-screen sheet; tapping the
   header field moves focus to the sheet's own field inside the same gesture (keyboard stays up).
   Tracking lives in header.js; this script dispatches "hdr-search:no-results" for it.
   ========================================================================== */
(() => {
  const panel = document.getElementById('HeaderSearchPanel');
  if (!panel || panel.dataset.hsInit) return;
  panel.dataset.hsInit = '1';

  const root = document.documentElement;
  const sticky = panel.closest('[data-hdr-sticky]');
  const backdrop = sticky && sticky.querySelector('[data-hs-backdrop]');
  const deskInput = document.querySelector('.maingrid-header-search [data-hdr-search-input]');
  const mobInput = document.querySelector('.mobile-search-form [data-hdr-search-input]');
  const sheetInput = panel.querySelector('[data-hs-sheet-input]');
  const sheetForm = panel.querySelector('[data-hs-sheet-form]');
  const focusView = panel.querySelector('[data-hs-focus]');
  const slot = panel.querySelector('[data-hs-results]');
  const allLink = panel.querySelector('[data-hs-all]');
  const allText = panel.querySelector('[data-hs-all-text]');
  const live = panel.querySelector('[data-hs-live]');
  const recentGroup = panel.querySelector('[data-hs-recent]');
  const recentList = panel.querySelector('[data-hs-recent-list]');
  const recentTpl = panel.querySelector('[data-hs-recent-tpl]');
  if (!sticky || !sheetInput) return;

  const mq = matchMedia('(max-width: 1023px)');
  const SEARCH_URL = panel.dataset.searchUrl || '/search';
  const PREDICT_URL = panel.dataset.url || '/search/suggest';
  const RECENT_KEY = 'crawford_recent_searches';
  const MIN_CHARS = 2, DEBOUNCE = 150;

  let input = null;          // the field driving the panel: desktop header field or the sheet field
  let isOpen = false, timer = 0, controller = null, shownTerm = '', activeOpt = null, suppressOpen = false, raf = 0;
  const cache = new Map();

  const searchHref = q => SEARCH_URL + '?q=' + encodeURIComponent(q);
  // "See all results" lands on product-only results, like the header forms (hidden type + options[prefix])
  const productHref = href => {
    try {
      const u = new URL(href, location.origin);
      u.searchParams.set('type', 'product');
      u.searchParams.set('options[prefix]', 'last');
      return u.pathname + u.search;
    } catch (e) { return href; }
  };
  const headerInputs = [deskInput, mobInput, sheetInput].filter(Boolean);

  /* ---- recent searches (localStorage, every access guarded) ---- */
  const readRecent = () => {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(v) ? v.filter(x => typeof x === 'string' && x.trim()).slice(0, 3) : [];
    } catch (e) { return []; }
  };
  const saveRecent = value => {
    const t = String(value || '').trim().slice(0, 60);
    if (!t) return;
    const list = [t].concat(readRecent().filter(x => x.toLowerCase() !== t.toLowerCase())).slice(0, 3);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
  };
  const renderRecent = () => {
    if (!recentGroup || !recentList || !recentTpl) return;
    const list = readRecent();
    recentList.replaceChildren();
    list.forEach((t, i) => {
      const a = recentTpl.content.firstElementChild.cloneNode(true);
      a.id = 'hs-recent-' + (i + 1);
      a.href = searchHref(t);
      const label = a.querySelector('[data-hs-label]');
      if (label) label.textContent = t;
      recentList.appendChild(a);
    });
    recentGroup.hidden = list.length === 0;
  };

  /* ---- online status for freshly inserted markup (header.js's support-hours script owns the clock) ---- */
  const syncOnline = scope => {
    const src = document.querySelector('[data-hdr-expert]');
    const on = !!(src && src.classList.contains('is-online'));
    const label = on && sticky.dataset.onlineLabel ? sticky.dataset.onlineLabel : (sticky.dataset.hoursLabel || '');
    scope.querySelectorAll('[data-hdr-expert]').forEach(el => el.classList.toggle('is-online', on));
    scope.querySelectorAll('[data-hdr-status]').forEach(el => { el.textContent = label; });
  };

  /* ---- options + keyboard ---- */
  const options = () => [...panel.querySelectorAll('[role="option"]')].filter(el => el.getClientRects().length > 0);
  const setActive = (el, scroll) => {
    if (activeOpt) activeOpt.classList.remove('is-active');
    activeOpt = el || null;
    if (activeOpt) {
      activeOpt.classList.add('is-active');
      if (input) input.setAttribute('aria-activedescendant', activeOpt.id);
      if (scroll) activeOpt.scrollIntoView({ block: 'nearest' });
    } else if (input) {
      input.removeAttribute('aria-activedescendant');
    }
  };
  const move = dir => {
    const list = options();
    if (!list.length) return;
    let i = list.indexOf(activeOpt);
    i = i === -1 ? (dir > 0 ? 0 : list.length - 1) : (i + dir + list.length) % list.length;
    setActive(list[i], true);
  };

  /* ---- states: focus (nothing typed) | results | none (no results) ---- */
  const setState = state => {
    panel.dataset.state = state;
    focusView.hidden = state !== 'focus';
    slot.hidden = state === 'focus';
    setActive(null);
  };
  const showFocus = () => { shownTerm = ''; renderRecent(); setState('focus'); live.textContent = ''; };

  const applyResults = (q, html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const res = doc.querySelector('[data-hs-root]');
    if (!res) return;
    syncOnline(res);
    slot.replaceChildren(document.adoptNode(res));
    shownTerm = q;
    const none = res.hasAttribute('data-no-results');
    allLink.href = productHref(res.dataset.allUrl || searchHref(q));
    allText.textContent = (panel.dataset.allLabel || '').replace('[terms]', q);
    setState(none ? 'none' : 'results');
    live.textContent = res.dataset.live || '';
    if (none) document.dispatchEvent(new CustomEvent('hdr-search:no-results', { detail: { term: q } }));
    place();
  };

  const fetchResults = q => {
    if (cache.has(q)) { applyResults(q, cache.get(q)); return; }
    if (controller) controller.abort();
    controller = new AbortController();
    const url = PREDICT_URL + '?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection,query&resources[limit]=10&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last&section_id=header-search-results';
    fetch(url, { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(html => {
        if (cache.size > 30) cache.delete(cache.keys().next().value);
        cache.set(q, html);
        // only the latest term wins; previous results stay visible until then
        if (isOpen && input && input.value.trim() === q) applyResults(q, html);
      })
      .catch(() => {});
  };

  const onInput = () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < MIN_CHARS) {
      if (controller) controller.abort();
      showFocus();
      return;
    }
    if (q === shownTerm) return;
    timer = setTimeout(() => fetchResults(q), DEBOUNCE);
  };

  /* ---- desktop placement: under the header field, inside the header container ---- */
  const place = () => {
    if (!isOpen) return;
    if (mq.matches) {
      panel.style.left = panel.style.top = panel.style.width = panel.style.maxHeight = '';
      return;
    }
    const form = deskInput && deskInput.form;
    if (!form) return;
    const fr = form.getBoundingClientRect();
    const sr = sticky.getBoundingClientRect();
    const box = (form.closest('.maingrid-header-top') || sticky).getBoundingClientRect();
    const width = Math.min(Math.max(fr.width, 780), box.width);
    let left = fr.left;
    if (left + width > box.right) left = box.right - width;
    left = Math.max(left, box.left);
    const top = fr.bottom + 10;
    panel.style.left = (left - sr.left) + 'px';
    panel.style.top = (top - sr.top) + 'px';
    panel.style.width = width + 'px';
    panel.style.maxHeight = Math.max(240, window.innerHeight - top - 16) + 'px';
    if (backdrop) backdrop.style.top = Math.max(0, sr.bottom) + 'px';
  };
  const schedulePlace = () => { if (isOpen && !raf) raf = requestAnimationFrame(() => { raf = 0; place(); }); };

  /* ---- open / close ---- */
  const setExpanded = on => headerInputs.forEach(el => el.setAttribute('aria-expanded', on ? 'true' : 'false'));

  function open(from) {
    if (suppressOpen) return;
    const mobile = mq.matches;
    const next = mobile ? sheetInput : deskInput;
    if (!next) return;
    if (mobile && from === mobInput) {
      sheetInput.value = mobInput.value;
    }
    input = next;
    if (!isOpen) {
      isOpen = true;
      panel.hidden = false;
      if (backdrop) backdrop.hidden = mobile;
      root.classList.add('hdr-search-open');
      setExpanded(true);
      place();
      const q = input.value.trim();
      if (q.length < MIN_CHARS) showFocus();
      else if (q !== shownTerm) { showFocus(); fetchResults(q); } // cached terms swap in at once
    }
    // mobile: move focus to the sheet field synchronously, inside the tap
    if (mobile && document.activeElement !== sheetInput) {
      sheetInput.focus();
      const n = sheetInput.value.length;
      try { sheetInput.setSelectionRange(n, n); } catch (e) {}
    }
  }

  function close(returnFocus) {
    if (!isOpen) return;
    isOpen = false;
    clearTimeout(timer);
    if (controller) controller.abort();
    setActive(null);
    panel.hidden = true;
    if (backdrop) backdrop.hidden = true;
    root.classList.remove('hdr-search-open');
    setExpanded(false);
    const wasSheet = input === sheetInput;
    if (wasSheet && mobInput) mobInput.value = sheetInput.value;
    if (wasSheet) {
      sheetInput.blur();
      if (returnFocus && mobInput) {
        suppressOpen = true;
        mobInput.focus();
        suppressOpen = false;
      }
    } else if (returnFocus && input) {
      suppressOpen = true;
      input.focus();
      suppressOpen = false;
    }
  }

  const onKeydown = e => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') { open(e.target); e.preventDefault(); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); move(1); break;
      case 'ArrowUp': e.preventDefault(); move(-1); break;
      case 'Escape': e.preventDefault(); close(true); break;
      case 'Enter':
        if (activeOpt) { e.preventDefault(); activeOpt.click(); }
        break; // no active option: the form submits normally
    }
  };

  /* ---- wiring ---- */
  if (deskInput) {
    deskInput.addEventListener('focus', () => open(deskInput));
    deskInput.addEventListener('click', () => open(deskInput));
    deskInput.addEventListener('input', () => { if (isOpen) onInput(); else open(deskInput); });
    deskInput.addEventListener('keydown', onKeydown);
  }
  if (mobInput) {
    mobInput.addEventListener('focus', () => open(mobInput));
    mobInput.addEventListener('click', () => open(mobInput));
  }
  sheetInput.addEventListener('input', onInput);
  sheetInput.addEventListener('keydown', onKeydown);

  const cancel = panel.querySelector('[data-hs-cancel]');
  if (cancel) cancel.addEventListener('click', () => close(false));
  if (backdrop) backdrop.addEventListener('click', () => close(false));

  // Remember what was searched: form submits and clicks on a suggestion or product
  [deskInput && deskInput.form, mobInput && mobInput.form, sheetForm].forEach(f => {
    if (f) f.addEventListener('submit', () => { const q = f.querySelector('input[name="q"]'); if (q) saveRecent(q.value); });
  });
  panel.addEventListener('click', e => {
    const a = e.target.closest && e.target.closest('a[data-hs-type]');
    if (!a) return;
    const type = a.getAttribute('data-hs-type');
    if (type === 'product') saveRecent(input ? input.value : '');
    else if (type === 'query') { const l = a.querySelector('[data-hs-label]'); saveRecent(l ? l.textContent : ''); }
  });
  // Mouse hover and keyboard share one active row
  panel.addEventListener('pointermove', e => {
    const opt = e.target.closest && e.target.closest('[role="option"]');
    if (opt && opt !== activeOpt && e.pointerType === 'mouse') setActive(opt);
  });

  // Click outside closes (the desktop field's own form counts as inside)
  document.addEventListener('pointerdown', e => {
    if (!isOpen) return;
    const t = e.target;
    if (panel.contains(t)) return;
    if (deskInput && deskInput.form && deskInput.form.contains(t) && !mq.matches) return;
    close(false);
  }, true);
  // Tab out closes; a null relatedTarget (e.g. the phone keyboard's Done) keeps the sheet open
  document.addEventListener('focusout', e => {
    if (!isOpen) return;
    const from = e.target, to = e.relatedTarget;
    if (!to) return;
    const inside = el => panel.contains(el) || (deskInput && el === deskInput) || (deskInput && deskInput.form && deskInput.form.contains(el));
    if (inside(from) && !inside(to)) close(false);
  });

  window.addEventListener('scroll', schedulePlace, { passive: true });
  window.addEventListener('resize', schedulePlace, { passive: true });
  mq.addEventListener('change', () => close(false));
})();
